<template>
  <IndicatorCard
    :loading="loading"
    :has-data="services.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhum serviço encontrado')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row>
      <v-col cols="12" sm="6" md="6" lg="6">
        <BaseEChart :option="barOption" :loading="loading" height="320px" />
      </v-col>
      <v-col cols="12" sm="6" md="6" lg="6">
        <BaseEChart :option="pieOption" :loading="loading" height="320px" />
      </v-col>
      <v-col cols="12">
        <div class="wrapper">
          <v-data-table
            :loading="loading"
            :loading-text="$t('Carregando...')"
            :no-data-text="$t('Nenhum dado encontrado')"
            :headers="headers"
            :items="services"
            class="main-table"
            :hide-default-footer="!filterProp.data"
            hide-default-header
            :header-props="headerProps"
            :items-per-page="-1"
          >
            <template #header="{ props, on }">
              <thead>
                <tr>
                  <th
                    class="text-center"
                    v-for="header in props.headers"
                    :key="header.value"
                  >
                    <v-simple-checkbox
                      v-if="header.value === 'data-table-select'"
                      v-model="props.everyItem"
                      :indeterminate="props.someItems && !props.everyItem"
                      color="purple"
                      @input="on['toggle-select-all']"
                    ></v-simple-checkbox>

                    <div>
                      <v-hover v-slot="{ hover }">
                        <div>
                          <span>{{ $t(header.text) }}</span>
                          <v-icon
                            v-if="
                              hover &&
                              header.value != 'data-table-select' &&
                              header.value != 'actions'
                            "
                            color="white"
                            class="mx-2"
                            @click="on.sort(header.value)"
                          >
                            {{
                              props.options.sortBy.includes(header.value)
                                ? `mdi-chevron-down`
                                : `mdi-chevron-up`
                            }}
                          </v-icon>
                        </div>
                      </v-hover>
                    </div>
                  </th>
                </tr>
              </thead>
            </template>

            <template v-slot:[`item.osType`]="{ item }">
              <span :class="{ 'subtype-row': item.isSubType }">
                <v-icon v-if="item.isSubType" small class="subtype-icon"
                  >mdi-subdirectory-arrow-right</v-icon
                >
                {{ item.serviceOrderType }}
              </span>
            </template>

            <template v-slot:[`item.solicited`]="{ item }">
              {{ item.totalRequestedServices }}
            </template>

            <template v-slot:[`item.conclued`]="{ item }">
              {{ item.totalCompletedServices }}
            </template>
          </v-data-table>
          <div class="pagination" v-if="!filterProp.data">
            <div class="item-pagination block-two">
              <v-pagination
                class="pagination-item"
                v-model="page"
                :length="totalPages"
                :total-visible="9"
              ></v-pagination>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :label="$t('Análise crítica')"
            :placeholder="
              $t('Digite sua análise crítica para o indicador abaixo')
            "
            :card-props="{ flat: true, outlined: true, rounded: true }"
            class="primary-input"
            outlined
            dense
            hide-details="auto"
          ></tiptap-vuetify>
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import BaseEChart from '../../shared/BaseEChart.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import {
  buildHorizontalBarOption,
  buildPieOption,
} from '../../shared/chartConfigs'
import { activityColors } from '../../shared/echartsTheme'
import moment from 'moment'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  components: {
    IndicatorCard,
    BaseEChart,
    DateRangePicker,
    TiptapVuetify,
  },

  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
    review: String,
  },

  data() {
    return {
      excelLoading: false,
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [Heading, { options: { levels: [1, 2, 3] } }],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak,
      ],
      filter: {},
      apiService: new ApiService(),
      loading: true,
      services: [],
      page: 1,
      totalPages: 1,
      total: 0,
      totalSolicited: 0,
      totalConclued: 0,
      labels: [],
      quantity: [],
      startDate: '',
      endDate: '',
      backgroundColors: [
        '#426483',
        '#BBCF20',
        '#0F3565',
        '#212950',
        '#C4E8F2',
        '#ED8990',
        '#FDB578',
        '#7EE882',
      ],
      headerProps: { sortByText: 'Ordenar por' },
      headers: [
        { text: '', align: 'center', value: '' },
        { text: 'Tipo O.S.', align: 'center', value: 'osType' },
        { text: 'Solicitadas', align: 'center', value: 'solicited' },
        { text: 'Concluídas', align: 'center', value: 'conclued' },
        { text: 'Pendentes', align: 'center', value: 'pendents' },
      ],
    }
  },

  computed: {
    indicator() {
      return getIndicatorById('solicited_v_concluded') || {}
    },
    barOption() {
      return buildHorizontalBarOption({
        categories: ['QTD.'],
        series: [
          {
            name: this.$t('Tot. Solicitadas'),
            data: [this.totalSolicited],
            color: '#212950',
          },
          {
            name: this.$t('Tot. Concluídas'),
            data: [this.totalConclued],
            color: '#BBCF20',
          },
        ],
        showLegend: true,
      })
    },

    pieOption() {
      return buildPieOption({
        data: this.labels.map((name, i) => ({
          name,
          value: this.quantity[i],
          color:
            activityColors[name] || activityColors[this.$t(name)] || '#7EE882',
        })),
        showLegend: true,
      })
    },
  },

  created() {
    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades

    const lastMonthStart = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')
    const lastMonthEnd = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.filter.startDate = lastMonthStart
    this.filter.endDate = lastMonthEnd
    this.startDate = lastMonthStart
    this.endDate = lastMonthEnd

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },

  methods: {
    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, {
          ...this.filter,
          pageSize: 999999,
        })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.filterProp.data = null
      this.list()
    },

    setInfos() {
      const model = {
        chart: getIndicatorById('solicited_v_concluded').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    switchValue(val) {
      switch (val) {
        case 0:
          return this.$t('Treinamento')
        case 1:
          return this.$t('Segurança elétrica')
        case 2:
          return this.$t('Preventiva')
        case 3:
          return this.$t('Instalação')
        case 4:
          return this.$t('Rotina de Inspeção')
        case 5:
          return this.$t('Corretiva')
        case 6:
          return this.$t('Calibração')
        case 7:
          return this.$t('Validação/Qualificação')
        case 8:
          return this.$t('Serviços Administrativos')
        case 9:
          return this.$t('Empréstimo')
        case 10:
          return this.$t('Tecnovigilancia')
        case 11:
          return this.$t('Obras')
        case 12:
          return this.$t('Serviço Operacional')
      }
    },

    getBackgroundColors(length) {
      this.backgroundColors = [
        '#426483',
        '#BBCF20',
        '#0F3565',
        '#212950',
        '#C4E8F2',
        '#ED8990',
        '#FDB578',
        '#7EE882',
      ]

      for (let i = 0; i < length; i++) {
        this.backgroundColors.push(
          `#${Math.floor(Math.random() * 16777215).toString(16)}`
        )
      }
    },

    extractChartData(response) {
      this.total = response.content.pager.totalItems
      this.totalPages = response.content.pager.totalPages
      this.services = []
      this.labels = []
      this.quantity = []
      this.totalSolicited = 0
      this.totalConclued = 0

      response.content.objects.map((value) => {
        value.serviceOrderType =
          value.displayName || this.switchValue(value.serviceOrderType)
        let pendents =
          value.totalPendingServices ??
          value.totalRequestedServices - value.totalCompletedServices
        this.services.push({ ...value, pendents })
      })

      this.getBackgroundColors(response.content.chartsModel.length)
      response.content.chartsModel.map((obj) => {
        this.labels.push(this.$t(obj.name))
        this.quantity.push(obj.quantity)
      })

      let totalSolicitedCounter = 0
      let totalCompletedCounter = 0

      response.content.objects
        .filter((obj) => !obj.isSubType)
        .map((obj) => {
          totalSolicitedCounter += obj.totalRequestedServices
          totalCompletedCounter += obj.totalCompletedServices
        })

      this.totalSolicited = totalSolicitedCounter
      this.totalConclued = totalCompletedCounter
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
          this.extractChartData(JSON.parse(this.filterProp.data))
          return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.pageSize = 7
        this.filter.unidadeIds = this.unidades

        const response = await this.apiService.post(
          this.apiEndpointOverride || 'reports/requestedCompletedServices',
          this.filter
        )
        this.extractChartData(response)
      } finally {
        this.loading = false
      }
    },

    printUniqueResults(arrayOfObj, key) {
      return arrayOfObj.filter((item, index, array) => {
        return array.map((mapItem) => mapItem[key]).indexOf(item[key]) === index
      })
    },
  },

  watch: {
    page() {
      this.filter.page = this.page
      this.list()
    },
    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.subtype-row {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 8px;
  color: #555;
  font-size: 0.9em;
}

.subtype-icon {
  margin-right: 4px;
  color: #888;
}
</style>
